import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useZomentumGrowQuoteStore } from "../../Store/ZomentumGrowQuote";
import { useTeamStore } from "../../Store/Team";
import { AdyenTransactionCreateRequest } from "../../Contracts/Transaction";
import MessageBanner from "../../Components/Molecules/PaymentsAppHeader/MessageBanner";
import { ReactComponent as PayingClipArt } from "../../Assets/ZomentumGrowQuotePayPage/PayingClipArt.svg";
import ZomentumLogo from "../../Assets/PaymentsAppHeader/ZomentumLogo.svg";
import Modal from "../../Components/Atoms/Modal";
import PaymentForm from "../../Components/Organisms/PaymentForm";

const ZomentumGrowQuotePayPage: React.FC = () => {
  const { id: sessionId } = useParams();
  const { formatCurrency } = useTeamStore();
  const [encounteredErrorDialogue, setEncounteredErrorDialogue] = useState<{
    open: boolean;
    error?: string;
  }>({ open: false });

  const {
    getGrowQuoteSessionDetails,
    growQuoteSessionView,
    createAdyenSession,
    adyenCheckoutSessionView,
    cancelPayment,
    doGiactValidation,
    createTransactionForQuote,
    getTransactionCreateResult,
    handleBacsPayment
  } = useZomentumGrowQuoteStore();

  const initialiseCheckoutPage = useCallback(async () => {
    if (sessionId) {
      await getGrowQuoteSessionDetails(sessionId);
    }
  }, [sessionId, getGrowQuoteSessionDetails]);

  useEffect(() => {
    initialiseCheckoutPage();
  }, [initialiseCheckoutPage]);

  const onCancelPaymentClick = async () => {
    if (sessionId) {
      await cancelPayment(sessionId);
    }
    if (growQuoteSessionView?.quote_url) {
      window.location.href = growQuoteSessionView.quote_url;
    }
  };

  const allowedPaymentMethods = growQuoteSessionView?.allowed_payment_methods || [];

  return (
    <>
      <MessageBanner />
      <Modal
        isOpen={encounteredErrorDialogue.open}
        onClose={() => {
          setEncounteredErrorDialogue({ open: false });
          if (growQuoteSessionView?.quote_url) {
            window.location.href = growQuoteSessionView.quote_url;
          }
        }}
        hideCancelButton
        onOk={() => {
          setEncounteredErrorDialogue({ open: false });
          if (growQuoteSessionView?.quote_url) {
            window.location.href = growQuoteSessionView.quote_url;
          }
        }}
        title="Error Processing Payment"
      >
        <div className="text-wrap">{encounteredErrorDialogue.error}</div>
      </Modal>

      <div className="grid grid-cols-5 h-screen">
        <div className="md:col-span-3 col-span-5 bg-white-smoke">
          <div className="flex space-x-2 m-4">
            {growQuoteSessionView?.partner_display_details?.branding_company_logo_s3_url && (
              <img
                src={growQuoteSessionView.partner_display_details.branding_company_logo_s3_url}
                alt="Company logo"
                className="max-h-10"
              />
            )}
            <div className="font-semibold">
              {growQuoteSessionView?.partner_display_details?.name}
            </div>
          </div>
          <div className="m-4 font-semibold">{growQuoteSessionView?.quote_title}</div>
          <div className="w-full flex justify-center my-48">
            <PayingClipArt />
          </div>
        </div>

        <div className="md:col-span-2 col-span-5 p-8">
          <div className="flex justify-between items-center">
            <div className="font-semibold text-lg">Review Payment</div>
            <button
              className="px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-500 hover:shadow-lg"
              onClick={onCancelPaymentClick}
            >
              Cancel Payment
            </button>
          </div>

          <div className="bg-white-smoke p-4 my-4 rounded">
            {!!growQuoteSessionView?.surcharge_amount.value &&
              growQuoteSessionView?.surcharge_amount.value > 0 && (
                <>
                  <div className="flex justify-between pb-2">
                    <div>Invoice amount</div>
                    <div>
                      {formatCurrency(
                        growQuoteSessionView.quote_amount.value,
                        growQuoteSessionView.quote_amount.currency
                      )}
                    </div>
                  </div>
                  <div className="flex justify-between border-b pb-3 mb-3">
                    <div>Transaction fee</div>
                    <div>
                      {formatCurrency(
                        growQuoteSessionView.surcharge_amount.value,
                        growQuoteSessionView.surcharge_amount.currency
                      )}
                    </div>
                  </div>
                </>
              )}
            <div className="flex justify-between font-semibold">
              <div>Amount Due</div>
              <div>
                {formatCurrency(
                  growQuoteSessionView?.total_amount?.value || 0,
                  growQuoteSessionView?.total_amount?.currency
                )}
              </div>
            </div>
          </div>

          <div className="py-4 font-semibold text-gray-700">Select Payment Method</div>

          <PaymentForm
            allowedPaymentMethods={allowedPaymentMethods}
            fetchAdyenSessionForMethod={async method => {
              if (sessionId) {
                await createAdyenSession(sessionId, method);
              }
            }}
            sessionData={adyenCheckoutSessionView?.session}
            callBacsPaymentRoute={async (data, transactionReference) => {
              if (sessionId) {
                return (await handleBacsPayment(transactionReference, data, sessionId)).success;
              }
              console.error("Unexpected error occured, there is no sessionId");
              return Promise.reject("Unexpected error occured, there is no sessionId");
            }}
            callTransactionRoute={async method => {
              if (
                !sessionId ||
                !adyenCheckoutSessionView?.transaction_data ||
                !adyenCheckoutSessionView?.transaction_data.splits
              ) {
                console.error("Missing session or transaction data");
                return Promise.reject("Missing session or transaction data");
              }
              const createTransactionRequest: AdyenTransactionCreateRequest = {
                method,
                session_uuid: sessionId,
                transaction: {
                  ...adyenCheckoutSessionView.transaction_data,
                  splits: adyenCheckoutSessionView.transaction_data.splits
                }
              };
              return await createTransactionForQuote(createTransactionRequest);
            }}
            callNotifyRoute={async resultCode => {
              if (adyenCheckoutSessionView?.transaction_data.reference) {
                await getTransactionCreateResult(
                  adyenCheckoutSessionView.transaction_data.reference,
                  resultCode
                );
              }
              if (growQuoteSessionView?.quote_url) {
                window.location.href = growQuoteSessionView.quote_url;
              }
            }}
            doGiactValidation={async (billingAddress, method) => {
              if (!sessionId) {
                console.error("Unexpected error occured, there is no sessionId");
                return Promise.reject("Unexpected error occured, there is no sessionId");
              }
              return await doGiactValidation({
                billing_address_data: billingAddress,
                payment_method: method,
                referrer: window.location.href,
                session_uuid: sessionId
              });
            }}
            onPaymentCompleted={() => console.log("Quote payment completed")}
          />

          <div className="text-gray-500 px-8 my-4 text-center">
            Your payment method will be stored securely by our payment processor and used to
            automatically collect future invoices and/or other charges.
          </div>

          <div className="flex items-center justify-center space-x-2 m-4 opacity-60">
            <span className="text-xs mt-0.5">Powered by </span>
            <img src={ZomentumLogo} alt="Zomentum Logo" className="w-20" />
          </div>
        </div>
      </div>
    </>
  );
};

export default ZomentumGrowQuotePayPage;
